import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';
import { errorMessage } from './ar';

/**
 * One fetching pattern for every screen.
 *
 * The behaviour that matters here is failure isolation: each section owns its own
 * request and its own error, so one failing call leaves the rest of the page intact
 * rather than replacing the whole dashboard with an error.
 */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  onAuthExpired: () => void,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const authRef = useRef(onAuthExpired);
  authRef.current = onAuthExpired;
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const result = await loadRef.current(controller.signal);
        if (cancelled) return;
        setData(result);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.isAuthExpiry) {
          authRef.current();
          return;
        }
        setError(errorMessage(caught instanceof ApiError ? caught.code : 'NETWORK'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, loading, error, reload };
}

/** Toasts, shared by the shell. */
export interface Toast {
  id: number;
  text: string;
  tone: 'default' | 'success' | 'critical';
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const push = useCallback((text: string, tone: Toast['tone'] = 'default') => {
    const id = (counter.current += 1);
    setToasts((previous) => [...previous, { id, text, tone }]);
    window.setTimeout(() => setToasts((previous) => previous.filter((t) => t.id !== id)), 3600);
  }, []);

  return { toasts, push };
}
